import { PublicLayout } from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Play, Users, Calendar, Clock, Star } from "lucide-react";
import { Link } from "react-router-dom";

const PublisherWerden = () => {
  return (
    <PublicLayout
      showHero={true}
      heroTitle="Publisher werden"
      heroSubtitle="Entdecken Sie, wie Sie mit unserer KI-Technologie in wenigen Minuten professionelle Hörbücher erstellen und veröffentlichen können."
      heroVariant="compact"
    >
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-background via-muted/30 to-background">
        <div className="container mx-auto text-center">
          <Badge variant="secondary" className="mb-6">
            Exklusiver Zugang
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Werden Sie <span className="text-empire-primary">Audio Empire</span> Publisher
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Erfahren Sie in unserer Live-Demonstration, wie Sie in wenigen Minuten 
            professionelle Hörbücher erstellen und auf allen wichtigen Plattformen veröffentlichen.
          </p>
          
          <div className="flex flex-col gap-4 justify-center mb-12 px-4 sm:px-0">
            <Button size="xl" variant="hero" className="w-full sm:w-auto">
              <Calendar className="mr-2 h-5 w-5" />
              Live Vorführung
            </Button>
            <Button size="xl" variant="outline" asChild className="w-full sm:w-auto">
              <Link to="/funktionen">
                Alle Funktionen ansehen
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              So funktioniert Audio Empire
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              In 3 einfachen Schritten von der Idee zum veröffentlichten Hörbuch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-empire-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Thema eingeben</h3>
                <p className="text-muted-foreground">
                  Geben Sie einfach Ihr gewünschtes Hörbuch-Thema ein. 
                  Unsere KI analysiert automatisch Marktchancen und Keywords.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-empire-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">KI erstellt Hörbuch</h3>
                <p className="text-muted-foreground">
                  Unsere emotionale KI-Stimme verwandelt Ihren Content in ein 
                  professionelles Hörbuch mit menschlicher Aussprache.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-card text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-empire-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-4">Automatische Veröffentlichung</h3>
                <p className="text-muted-foreground">
                  Mit einem Klick wird Ihr Hörbuch auf Audible, Spotify, 
                  Apple Podcasts und allen wichtigen Plattformen veröffentlicht.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live Demo Info */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-elegant">
              <CardContent className="p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Play className="h-6 w-6 text-empire-primary" />
                      <Badge variant="secondary">Live Demo</Badge>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-4">
                      Nächste Live Produkt-Präsentation
                    </h3>
                    
                    <p className="text-muted-foreground mb-6">
                      Erleben Sie live, wie ein komplettes Hörbuch in wenigen Minuten erstellt 
                      und veröffentlicht wird. Unsere Experten zeigen Ihnen alle Funktionen 
                      und beantworten Ihre Fragen.
                    </p>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-empire-success" />
                        <span className="text-sm">Live Hörbuch-Erstellung in Echtzeit</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-empire-success" />
                        <span className="text-sm">Exklusive Tipps für Bestseller</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-empire-success" />
                        <span className="text-sm">Q&A Session mit den Entwicklern</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-5 w-5 text-empire-success" />
                        <span className="text-sm">Limitierte Lizenz-Vergabe</span>
                      </div>
                    </div>

                    <Button size="lg" variant="hero" className="w-full">
                      <Users className="mr-2 h-5 w-5" />
                      <span className="text-sm sm:text-base">Jetzt kostenlos anmelden</span>
                    </Button>
                  </div>

                  <div className="bg-gradient-to-br from-empire-primary/10 to-empire-accent/10 rounded-lg p-6">
                    <div className="text-center">
                      <Clock className="h-12 w-12 text-empire-primary mx-auto mb-4" />
                      
                      <div className="space-y-4">
                        <div>
                          <div className="text-sm text-muted-foreground">Nächster Termin</div>
                          <div className="text-xl font-bold">Morgen, 19:00 Uhr</div>
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Dauer</div>
                          <div className="text-lg font-semibold">60 Minuten</div>
                        </div>
                        
                        <div>
                          <div className="text-sm text-muted-foreground">Verfügbare Plätze</div>
                          <div className="text-lg font-semibold text-empire-warning">Nur noch 12 frei</div>
                        </div>

                        <div className="pt-4">
                          <div className="flex items-center justify-center space-x-1 text-sm text-muted-foreground">
                            <Star className="h-4 w-4 text-empire-warning fill-current" />
                            <span>4.9/5 Bewertung von Teilnehmern</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-12">
            Was unsere Publisher sagen
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-empire-warning fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "In 3 Wochen habe ich 5 Hörbücher veröffentlicht und bereits über 
                  €2.000 Umsatz gemacht. Audio Empire ist revolutionär!"
                </p>
                <div className="text-sm font-medium">Maria S., Autorin</div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-empire-warning fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Die KI-Stimmen sind so natürlich, dass meine Hörer denken, 
                  ich hätte ein professionelles Studio benutzt."
                </p>
                <div className="text-sm font-medium">Thomas K., Unternehmer</div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-empire-warning fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Endlich kann ich meine Ideen schnell umsetzen. Von der Idee 
                  zum Bestseller in weniger als einer Woche!"
                </p>
                <div className="text-sm font-medium">Sarah L., Coach</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-hero">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Starten Sie Ihr Hörbuch-Empire noch heute
          </h2>
          <p className="text-xl text-foreground/80 mb-8 max-w-2xl mx-auto">
            Limitierte Lizenzen verfügbar. Sichern Sie sich Ihren Platz 
            in der nächsten Live-Demonstration.
          </p>
          
          <Button size="xl" variant="secondary" className="w-full sm:w-auto max-w-sm mx-auto">
            <Calendar className="mr-2 h-5 w-5" />
            <span className="text-sm sm:text-base">Live Vorführung</span>
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default PublisherWerden;